/* eslint-disable @typescript-eslint/no-explicit-any */
// HistoryDialogContext.tsx
import {
 Box,
 Table,
 TableBody,
 TableCell,
 TableContainer,
 TableHead,
 TableRow,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React, {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import CloseButton from '../components/common/CloseButton';
import { getHistoryStart } from '../redux/history/historySlice';
import { RootState } from '../redux/store';

// Define types for dialog state and the context function
interface HistoryDialogProps {
 row: any;
 isOpen: boolean;
 onConfirm: () => void;
}

interface HistoryDialogContextType {
 openDialog: ({ row, onConfirm }: Omit<HistoryDialogProps, 'isOpen'>) => void;
}

// Create the context with proper types
const HistoryDialogContext = createContext<
 HistoryDialogContextType | undefined
>(undefined);

export const HistoryDialogProvider = ({
 children,
}: {
 children: ReactNode;
}) => {
 const dispatch = useDispatch();
 const selectorHistory = useSelector((state: RootState) => state.history);
 const { loading, success, history } = selectorHistory;

 const [dialogState, setDialogState] = useState<HistoryDialogProps>({
  row: undefined,
  isOpen: false,
  onConfirm: () => {},
 });
 const { row: tableRow } = dialogState;

 const openDialog = useCallback(
  ({ row, onConfirm }: Omit<HistoryDialogProps, 'isOpen'>) => {
   setDialogState({
    row,
    isOpen: true,
    onConfirm,
   });
  },
  [],
 );

 const closeDialog = useCallback(() => {
  setDialogState((prevState) => ({ ...prevState, isOpen: false }));
 }, []);

 const confirmAction = useCallback(() => {
  if (dialogState.onConfirm) dialogState.onConfirm();
  closeDialog();
 }, [dialogState.onConfirm, closeDialog]);

 // Memoize the value for the context provider
 const contextValue = useMemo(
  () => ({
   openDialog,
  }),
  [openDialog],
 );

 const {
  handleSubmit,
  control,
  formState: { errors },
  setValue,
  register,
  reset,
  watch,
 } = useForm({
  mode: 'onChange',
 });

 useEffect(() => {
  // if (success?.HistorysGeneratePDF) {
  //  if (History.file && History.type && History.extension && History.name) {
  //   downloadBase64File(History.file, History.name, History.type, History.extension);
  //  }
  // }
 }, [success]);

 useEffect(() => {
  // console.log('History first ue', tableRow);
  if (tableRow?.Id) {
   dispatch(getHistoryStart({ model: 'Result', id: tableRow.Id }));
  }
  // if()
 }, [tableRow]);

 return (
  <HistoryDialogContext.Provider value={contextValue}>
   {children}
   {dialogState.isOpen && (
    <Dialog
     open={dialogState.isOpen}
     onClose={closeDialog}
     sx={{
      '& .MuiDialog-container': {
       '& .MuiPaper-root': {
        width: '100%',
        overflowY: 'hidden',
        //  height: '100%',
        maxWidth: '900px', // 960
       },
      },
     }}
    >
     <DialogTitle>i.VAZ</DialogTitle>
     <CloseButton closeDialog={closeDialog} />
     <DialogContent>
      <Box
       sx={{
        // display: 'flex',
        // alignItems: 'center',
        gap: 1,
        py: 2,
        width: '100%',
       }}
      >
       <TableContainer sx={{ mb: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
         <TableHead>
          <TableRow>
           <TableCell>Data</TableCell>
           <TableCell>Laukas</TableCell>
           <TableCell>Iš</TableCell>
           <TableCell>Į</TableCell>
          </TableRow>
         </TableHead>
         <TableBody>
          {history &&
           history?.map((h) => (
            <TableRow
             key={h.Id}
             sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
             <TableCell>{h.Date}</TableCell>
             <TableCell>{h.Column}</TableCell>
             <TableCell>{h?.ChangeFrom}</TableCell>
             <TableCell>{h?.ChangeTo}</TableCell>
            </TableRow>
           ))}
         </TableBody>
        </Table>
       </TableContainer>
      </Box>
     </DialogContent>
    </Dialog>
   )}
  </HistoryDialogContext.Provider>
 );
};

export const useHistoryDialog = (): HistoryDialogContextType => {
 const context = useContext(HistoryDialogContext);
 if (!context) {
  throw new Error(
   'useHistoryDialog must be used within a HistoryDialogProvider',
  );
 }
 return context;
};
